import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  ImageIcon, 
  Upload, 
  Download, 
  RefreshCw, 
  ArrowLeft, 
  Sliders, 
  Lock, 
  Check, 
  Trash2, 
  MousePointer, 
  Eye, 
  Layers, 
  Info
} from 'lucide-react';

export const BackgroundRemover = () => {
  const { withAd, toast } = useAdRedirect();
  const [imageFile, setImageFile] = useState(null);
  const [originalUrl, setOriginalUrl] = useState('');
  const [processedUrl, setProcessedUrl] = useState('');
  
  // Image metadata
  const [meta, setMeta] = useState({
    name: '',
    originalWidth: 0,
    originalHeight: 0,
    format: ''
  });

  // Background removal controls
  const [mode, setMode] = useState('white'); // 'white', 'black', 'custom'
  const [customColor, setCustomColor] = useState('#ffffff');
  const [tolerance, setTolerance] = useState(20); // 0 to 100
  const [smoothness, setSmoothness] = useState(10); // 0 to 100
  const [sampleModeActive, setSampleModeActive] = useState(false);

  // UI state
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);
  
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const workspaceImgRef = useRef(null);

  // Clean up ObjectURLs to avoid memory leaks
  useEffect(() => {
    return () => {
      if (originalUrl) URL.revokeObjectURL(originalUrl);
    };
  }, [originalUrl]);

  useEffect(() => {
    return () => {
      if (processedUrl) URL.revokeObjectURL(processedUrl);
    };
  }, [processedUrl]);

  // Drag & Drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const processFile = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please drop a valid image file (PNG, JPG, WebP).');
      return;
    }

    // Revoke previous URLs before creating new ones
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    if (processedUrl) URL.revokeObjectURL(processedUrl);

    const prevUrl = URL.createObjectURL(file);
    setOriginalUrl(prevUrl);
    setProcessedUrl('');
    setImageFile(file);
    setIsDownloaded(false);
    setSampleModeActive(false);

    // Load dimensions
    const img = new Image();
    img.onload = () => {
      setMeta({
        name: file.name,
        originalWidth: img.naturalWidth,
        originalHeight: img.naturalHeight,
        format: file.type.split('/')[1] || 'png'
      });
    };
    img.src = prevUrl;
  };

  // Click on image to sample custom color
  const handleImageClick = (e) => {
    if (!sampleModeActive || !workspaceImgRef.current) return;

    const imgEl = workspaceImgRef.current;
    const rect = imgEl.getBoundingClientRect();
    
    // Calculate click coordinates relative to raw natural dimensions
    const x = Math.floor(((e.clientX - rect.left) / rect.width) * imgEl.naturalWidth);
    const y = Math.floor(((e.clientY - rect.top) / rect.height) * imgEl.naturalHeight);

    // Draw image onto virtual canvas to sample pixel color
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = imgEl.naturalWidth;
    tempCanvas.height = imgEl.naturalHeight;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;

    tempCtx.drawImage(imgEl, 0, 0);
    try {
      const pixel = tempCtx.getImageData(x, y, 1, 1).data;
      const r = pixel[0];
      const g = pixel[1];
      const b = pixel[2];
      
      // Convert RGB to HEX
      const rgbToHex = (r, g, b) => '#' + [r, g, b].map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      }).join('');

      setCustomColor(rgbToHex(r, g, b));
      setMode('custom');
      setSampleModeActive(false); // turn off eyedropper
    } catch (err) {
      console.error("Failed to sample pixel color:", err);
    }
  };

  // Core canvas background removal keying algorithm
  const executeBackgroundRemoval = (srcUrl, currentMode, currentCustomColor, currentTolerance, currentSmoothness) => {
    if (!srcUrl) return;

    setIsProcessing(true);
    const img = new Image();
    
    img.onload = () => {
      const canvas = canvasRef.current || document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setIsProcessing(false);
        return;
      }

      const w = img.naturalWidth;
      const h = img.naturalHeight;
      canvas.width = w;
      canvas.height = h;

      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0);

      try {
        const imgData = ctx.getImageData(0, 0, w, h);
        const data = imgData.data;

        // Determine target key color in RGB
        let targetR = 255;
        let targetG = 255;
        let targetB = 255;

        if (currentMode === 'black') {
          targetR = 0;
          targetG = 0;
          targetB = 0;
        } else if (currentMode === 'custom') {
          // Parse HEX string to RGB
          const hex = currentCustomColor.replace('#', '');
          targetR = parseInt(hex.substring(0, 2), 16) || 0;
          targetG = parseInt(hex.substring(2, 4), 16) || 0;
          targetB = parseInt(hex.substring(4, 6), 16) || 0;
        }

        // Compute tolerances
        const tolRange = (currentTolerance / 100) * 441.67; // Maximum 3D Euclidean distance (sqrt(255^2 * 3) = 441.67)
        const smoothRange = (currentSmoothness / 100) * 100; // anti-aliasing feathering range

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];

          if (a === 0) continue; // skip already transparent pixels

          // Euclidean distance in color space
          const dist = Math.sqrt(
            Math.pow(r - targetR, 2) + 
            Math.pow(g - targetG, 2) + 
            Math.pow(b - targetB, 2)
          );

          // Apply feathered thresholding
          if (dist < tolRange) {
            if (smoothRange === 0) {
              data[i + 3] = 0; // transparent
            } else {
              // Smooth transition fade mapping
              const ratio = dist / tolRange;
              if (ratio < (1 - smoothRange / 100)) {
                data[i + 3] = 0; // completely transparent
              } else {
                const alphaFactor = (ratio - (1 - smoothRange / 100)) / (smoothRange / 100);
                data[i + 3] = Math.min(a, Math.round(a * alphaFactor));
              }
            }
          }
        }

        ctx.putImageData(imgData, 0, 0);

        // Export as transparent PNG
        canvas.toBlob((blob) => {
          if (blob) {
            setProcessedUrl((prev) => {
              if (prev) URL.revokeObjectURL(prev);
              return URL.createObjectURL(blob);
            });
          }
          setIsProcessing(false);
        }, 'image/png');

      } catch (err) {
        console.error("Background keying failed:", err);
        setIsProcessing(false);
      }
    };

    img.onerror = () => {
      console.error("Failed to load image for background removal");
      setIsProcessing(false);
    };

    img.src = srcUrl;
  };

  // Re-run keyer whenever tolerance/smoothness/colors change
  useEffect(() => {
    if (imageFile && originalUrl) {
      const timer = setTimeout(() => {
        executeBackgroundRemoval(originalUrl, mode, customColor, tolerance, smoothness);
      }, 150); // debounce slider shifts
      return () => clearTimeout(timer);
    }
  }, [imageFile, originalUrl, mode, customColor, tolerance, smoothness]);

  const handleDownload = () => {
    if (!processedUrl) return;

    const link = document.createElement('a');
    const origBaseName = meta.name.substring(0, meta.name.lastIndexOf('.')) || meta.name;
    link.download = `${origBaseName}-removed-bg.png`;
    link.href = processedUrl;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setIsDownloaded(true);
    setTimeout(() => setIsDownloaded(false), 3000);
  };

  const handleReset = () => {
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    if (processedUrl) URL.revokeObjectURL(processedUrl);
    setImageFile(null);
    setOriginalUrl('');
    setProcessedUrl('');
    setMeta({
      name: '',
      originalWidth: 0,
      originalHeight: 0,
      format: ''
    });
    setMode('white');
    setTolerance(20);
    setSmoothness(10);
    setSampleModeActive(false);
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      <SEO 
        title="Background Remover - Remove Background From Image Free" 
        description="Free background remover. Instantly remove background from image free online. Create transparent PNGs securely in your browser with no uploads."
        keywords="background remover, remove background from image free, transparent png maker, image background eraser, offline background removal"
      />
      
      {/* Offscreen Canvas for background chroma removal */}
      <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>

      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Remove Background</span>
      </div>



      {/* Main Container */}
      {!imageFile ? (
        /* Drag & Drop Upload State */
        <div 
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: dragActive ? '2.5px dashed var(--primary)' : '2px dashed var(--border-color)',
            background: dragActive ? 'rgba(5, 117, 251, 0.02)' : '#ffffff',
            borderRadius: '24px',
            padding: '80px 40px',
            textAlign: 'center',
            cursor: 'pointer',
            boxShadow: dragActive ? 'var(--shadow-md)' : 'var(--shadow-sm)',
            transition: 'var(--transition-smooth)',
            maxWidth: '800px',
            margin: '0 auto'
          }}
          className="drag-drop-compressor"
        >
          <input 
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/png, image/jpeg, image/jpg, image/webp"
            style={{ display: 'none' }}
          />

          <div style={{
            background: 'rgba(5, 117, 251, 0.05)',
            color: 'var(--primary)',
            width: '68px',
            height: '68px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto'
          }}>
            <Upload size={32} />
          </div>

          <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
            Drag and Drop Image here
          </h3>
          <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', maxWidth: '420px', margin: '0 auto 20px auto', lineHeight: 1.5 }}>
            Upload PNG, JPG, JPEG, or WebP. We will convert your output to a transparent PNG file.
          </p>
          <button style={{
            background: 'var(--primary)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '10px',
            padding: '12px 24px',
            fontSize: '0.9rem',
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'var(--transition-smooth)',
            boxShadow: '0 4px 12px -2px rgba(5, 117, 251, 0.25)'
          }} className="browse-files-btn">
            Browse Image Files
          </button>
        </div>
      ) : (
        /* Workspace Active State */
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '32px',
          alignItems: 'start'
        }} className="remover-workspace-grid">
          
          {/* Left Column: Side-by-Side Comparison & Eyedropper Workspace */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            
            {/* Display Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px'
            }}>
              {/* Original Sample Card with Eyedropper Interaction */}
              <div style={cardStyle}>
                <div style={cardHeaderStyle}>
                  <span style={headerLabelStyle}>Original & Color Sampler</span>
                  <button
                    onClick={() => setSampleModeActive(!sampleModeActive)}
                    style={{
                      border: 'none',
                      background: sampleModeActive ? 'var(--primary)' : 'rgba(15, 23, 42, 0.04)',
                      color: sampleModeActive ? '#ffffff' : 'var(--text-secondary)',
                      fontSize: '0.75rem',
                      fontWeight: 800,
                      padding: '6px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'var(--transition-smooth)'
                    }}
                    title="Click here, then click any color on the image below to key it out!"
                  >
                    <MousePointer size={12} /> {sampleModeActive ? 'Sample Mode: ON' : 'Eyedropper Sample'}
                  </button>
                </div>
                
                {/* Frame wrapper */}
                <div style={{
                  ...imgFrameStyle,
                  cursor: sampleModeActive ? 'crosshair' : 'default',
                  border: sampleModeActive ? '2px solid var(--primary)' : '1px solid var(--border-color)',
                  boxShadow: sampleModeActive ? '0 0 15px rgba(5, 117, 251, 0.15)' : 'none'
                }}>
                  {sampleModeActive && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      background: 'var(--primary)',
                      color: '#ffffff',
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      padding: '4px 8px',
                      borderRadius: '4px',
                      zIndex: 10,
                      pointerEvents: 'none'
                    }}>
                      Click any pixel to sample background color!
                    </div>
                  )}
                  
                  <img 
                    ref={workspaceImgRef}
                    src={originalUrl} 
                    alt="Sample input"
                    onClick={handleImageClick}
                    style={{
                      ...previewImgStyle,
                      pointerEvents: 'auto'
                    }} 
                  />
                </div>

                <div style={metaFooterStyle}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                    Click "Eyedropper Sample" to lock customized hues.
                  </span>
                </div>
              </div>

              {/* Processed Alpha Checkerboard Card */}
              <div style={{ ...cardStyle, border: '1px solid rgba(5, 117, 251, 0.2)', boxShadow: '0 10px 30px -12px rgba(5, 117, 251, 0.08)' }}>
                <div style={cardHeaderStyle}>
                  <span style={headerLabelStyle}>Transparent PNG Output</span>
                  <span style={{ ...badgeStyle, background: 'rgba(5, 117, 251, 0.08)', color: 'var(--primary)' }}>
                    Auto PNG Conversion
                  </span>
                </div>

                <div style={imgFrameStyle}>
                  {isProcessing ? (
                    <div style={loadingOverlayStyle}>
                      <RefreshCw size={28} className="spinner" style={{ color: 'var(--primary)' }} />
                      <p style={{ fontSize: '0.8rem', fontWeight: 700, margin: '8px 0 0 0', color: 'var(--text-secondary)' }}>Removing background...</p>
                    </div>
                  ) : (
                    <img src={processedUrl || originalUrl} alt="Transformed output" style={previewImgStyle} />
                  )}
                </div>

                <div style={metaFooterStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.825rem' }}>
                    <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Format:</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 800 }}>PNG (With Alpha channel)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Client sandbox info banner */}
            <div style={{
              background: '#ffffff',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '20px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <Info size={16} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: '2px' }} />
              <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                <strong>How it works:</strong> The keyer checks each pixel's color distance from your selected background color. Adjusting <strong>Tolerance</strong> controls how aggressively similar shades are removed. Adjusting <strong>Feather Smoothness</strong> creates beautiful anti-aliased transitions along the cut edges!
              </p>
            </div>
          </div>

          {/* Right Column: Controls card */}
          <div style={{
            position: 'sticky',
            top: '110px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            width: '100%',
            maxWidth: '380px',
            margin: '0 auto'
          }} className="compressor-control-column">
            
            <div style={{
              background: '#ffffff',
              border: '1px solid var(--border-color)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: 'var(--shadow-md)'
            }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sliders size={18} style={{ color: 'var(--primary)' }} /> Keyer Parameters
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                
                {/* 1. Target Color Mode Selector */}
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Target Background Color</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                    {['white', 'black', 'custom'].map((m) => (
                      <button
                        key={m}
                        onClick={() => setMode(m)}
                        style={{
                          height: '38px',
                          border: mode === m ? '1.5px solid var(--primary)' : '1px solid var(--border-color)',
                          borderRadius: '8px',
                          fontSize: '0.78rem',
                          fontWeight: 800,
                          cursor: 'pointer',
                          background: mode === m ? 'rgba(5, 117, 251, 0.05)' : '#ffffff',
                          color: mode === m ? 'var(--primary)' : 'var(--text-secondary)',
                          textTransform: 'uppercase',
                          transition: 'var(--transition-smooth)'
                        }}
                      >
                        {m === 'custom' ? 'Custom' : m}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom hex selector */}
                {mode === 'custom' && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    padding: '10px 14px',
                    borderRadius: '8px'
                  }}>
                    <input 
                      type="color" 
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      style={{
                        border: 'none',
                        outline: 'none',
                        width: '32px',
                        height: '32px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        padding: 0,
                        background: 'transparent'
                      }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Selected Hex Color</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, fontFamily: 'monospace', color: 'var(--text-primary)' }}>{customColor.toUpperCase()}</span>
                    </div>
                  </div>
                )}

                {/* 2. Tolerance control */}
                <div style={inputGroupStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={labelStyle}>Color Tolerance</label>
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--primary)', background: 'rgba(5, 117, 251, 0.06)', padding: '2px 8px', borderRadius: '6px' }}>
                      {tolerance}%
                    </span>
                  </div>
                  <input 
                    type="range"
                    min="1"
                    max="100"
                    step="1"
                    value={tolerance}
                    onChange={(e) => setTolerance(parseInt(e.target.value))}
                    style={{ width: '100%', accentColor: 'var(--primary)', cursor: 'pointer' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                    <span>Strict</span>
                    <span>Aggressive</span>
                  </div>
                </div>

                {/* 3. Edge Smoothness control */}
                <div style={inputGroupStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={labelStyle}>Feather Smoothness</label>
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--secondary)', background: 'rgba(14, 165, 233, 0.06)', padding: '2px 8px', borderRadius: '6px' }}>
                      {smoothness}%
                    </span>
                  </div>
                  <input 
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={smoothness}
                    onChange={(e) => setSmoothness(parseInt(e.target.value))}
                    style={{ width: '100%', accentColor: 'var(--secondary)', cursor: 'pointer' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                    <span>Jagged Cut</span>
                    <span>Smooth Blend</span>
                  </div>
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '4px 0' }} />

                {/* Actions */}
                <AdToast message={toast} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button
                    onClick={withAd('bg-remove', handleDownload)}
                    disabled={isProcessing || !processedUrl}
                    style={{
                      background: isDownloaded ? '#059669' : 'linear-gradient(90deg, var(--secondary) 0%, var(--primary) 100%)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '14px',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      cursor: (isProcessing || !processedUrl) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 6px 18px -2px rgba(5, 117, 251, 0.3)',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="download-compressed-btn"
                  >
                    {isDownloaded ? <Check size={18} /> : <Download size={18} />}
                    {isDownloaded ? 'Downloaded Transparent PNG!' : 'Download Transparent PNG'}
                  </button>

                  <button
                    onClick={handleReset}
                    style={{
                      background: 'rgba(15, 23, 42, 0.03)',
                      color: 'var(--text-primary)',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="reset-compressor-btn"
                  >
                    <RefreshCw size={14} /> Clear & Sample New
                  </button>
                </div>

              </div>
            </div>



          </div>
        </div>
      )}

      {/* Workspace design styling injections */}
      <style>{`
        @media (min-width: 992px) {
          .remover-workspace-grid {
            grid-template-columns: 1fr 380px !important;
          }
        }

        .drag-drop-compressor {
          transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s !important;
        }
        .drag-drop-compressor:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md) !important;
          border-color: var(--primary) !important;
        }

        .browse-files-btn:hover {
          background: var(--primary-hover) !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px -2px rgba(5, 117, 251, 0.4) !important;
        }

        .download-compressed-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 20px -2px rgba(5, 117, 251, 0.45) !important;
          opacity: 0.95;
        }

        .reset-compressor-btn:hover {
          background: rgba(15, 23, 42, 0.06) !important;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="remove-background" />
    </div>
  );
};

// Structural layouts
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  boxShadow: 'var(--shadow-sm)'
};

const cardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const headerLabelStyle = {
  fontSize: '0.85rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const badgeStyle = {
  fontSize: '0.725rem',
  padding: '4px 10px',
  borderRadius: '99px',
  fontWeight: 700
};

const imgFrameStyle = {
  background: 'repeating-conic-gradient(#f8fafc 0% 25%, #f1f5f9 0% 50%) 50% / 20px 20px', // transparency checkerboard
  border: '1px solid var(--border-color)',
  borderRadius: '12px',
  aspectRatio: '16/10',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  position: 'relative'
};

const previewImgStyle = {
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain'
};

const loadingOverlayStyle = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(255, 255, 255, 0.85)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
};

const metaFooterStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  paddingTop: '8px',
  borderTop: '1px solid var(--border-color)'
};

const inputGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

export default BackgroundRemover;
